import "../foundation/ctl-divider.js";

const template = document.createElement("template");
template.innerHTML = `
<style>
  :host{
    display:block;
    font-family: var(--ctl-font-sans, system-ui, -apple-system, Segoe UI, Roboto, Arial);
    color: var(--ctl-color-text);
    min-width: 0;
    box-sizing: border-box;
  }

  .card{
    border-radius: var(--ctl-radius-lg);
    background: var(--ctl-color-surface);
    border: 1px solid transparent;
    overflow: hidden;
    position: relative;
    min-width: 0;
    transition: transform var(--ctl-transition-fast), box-shadow var(--ctl-transition-fast), border-color var(--ctl-transition-fast), background var(--ctl-transition-fast);
  }

  /* Variants */
  :host(:not([variant]), [variant="outlined"], [variant="surface"]) .card{
    border-color: var(--ctl-color-border);
    box-shadow: none;
  }

  :host([variant="filled"]) .card{
    border-color: transparent;
    box-shadow: none;
    background: var(--ctl-color-surface);
  }

  :host([variant="elevated"]) .card{
    border-color: var(--ctl-color-border);
    border-color: color-mix(in srgb, var(--ctl-color-border) 60%, transparent);
    box-shadow: var(--ctl-shadow-card);
  }

  :host([variant="ghost"]) .card{
    border-color: transparent;
    background: transparent;
    box-shadow: none;
  }

  :host([variant="inset"]) .card{
    border-color: var(--ctl-color-border);
    background: color-mix(in srgb, var(--ctl-color-surface-2, #f9fafb) 92%, var(--ctl-color-surface));
    box-shadow: none;
  }

  /* Intent - border color accent */
  :host([intent="primary"]) .card{
    border-color: var(--ctl-color-brand);
  }
  :host([intent="secondary"]) .card{
    border-color: var(--ctl-color-secondary, var(--ctl-color-brand));
  }
  :host([intent="success"]) .card{
    border-color: var(--ctl-color-success);
  }
  :host([intent="warning"]) .card{
    border-color: var(--ctl-color-warning);
  }
  :host([intent="danger"]) .card{
    border-color: var(--ctl-color-danger);
  }

  /* Border toggle */
  :host([border="false"]) .card{
    border: 0 !important;
  }

  /* Radius */
  :host([radius="sm"]) .card{ border-radius: var(--ctl-radius-sm); }
  :host([radius="md"]) .card{ border-radius: var(--ctl-radius-md); }
  :host([radius="lg"]) .card{ border-radius: var(--ctl-radius-lg); }

  /* Padding (applies to sections) */
  :host(:not([padding]), [padding="md"]) { --pad: var(--ctl-space-3); }
  :host([padding="none"]) { --pad: 0; }
  :host([padding="sm"]) { --pad: var(--ctl-space-2); }
  :host([padding="lg"]) { --pad: var(--ctl-space-4); }

  .header, .body, .footer{
    padding: var(--pad);
  }

  /* Header/footer are hidden by default; shown via JS (_syncSlots) when slot has assigned nodes */
  .header{
    display: none;
    border-bottom: 1px solid var(--ctl-color-border);
    border-bottom: 1px solid color-mix(in srgb, var(--ctl-color-border) 80%, transparent);
    background: var(--ctl-color-surface);
    background: color-mix(in srgb, var(--ctl-color-surface) 96%, black 0%);
  }
  :host([has-header]) .header{ display: block; }

  .footer{
    display: none;
    border-top: 1px solid var(--ctl-color-border);
    border-top: 1px solid color-mix(in srgb, var(--ctl-color-border) 80%, transparent);
    background: var(--ctl-color-surface);
    background: color-mix(in srgb, var(--ctl-color-surface) 96%, black 0%);
  }
  :host([has-footer]) .footer{ display: block; }

  /* Dividers: only render adjacent to sections that actually have content */
  .divider{ display:none; }
  :host([dividers][has-header]) [part="divider-header"]{ display:block; }
  :host([dividers][has-footer]) [part="divider-footer"]{ display:block; }

  /* When dividers are shown, hide the CSS borders on header/footer to avoid double lines
     (the header/footer borders are defined above with border-bottom and border-top) */
  :host([dividers]) .header{ border-bottom: none; }
  :host([dividers]) .footer{ border-top: none; }

  /* Hoverable - subtle hover effect without interactivity */
  :host([hoverable]) .card{
    cursor: default;
  }
  :host([hoverable]) .card:hover{
    border-color: var(--ctl-color-border-2);
    box-shadow: var(--ctl-shadow-card-hover);
    transform: translateY(-2px);
  }

  /* Interactive - full interactivity with click handlers */
  :host([interactive]) .card{
    cursor: pointer;
  }
  :host([interactive]) .card:hover{
    border-color: var(--ctl-color-border);
    border-color: color-mix(in srgb, var(--ctl-color-border) 65%, transparent);
    box-shadow: var(--ctl-shadow-card-hover);
    transform: translateY(-1px);
  }
  :host([interactive]) .card:focus-visible{
    outline: none;
    box-shadow: var(--ctl-shadow-card-hover), var(--ctl-focus-ring);
  }

  /* Disabled */
  :host([disabled]){ opacity: var(--ctl-opacity-disabled, 0.5); }
  :host([disabled][interactive]) .card{
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }
</style>

<section class="card" part="card">
  <header class="header" part="header"><slot name="header"></slot></header>
  <ctl-divider class="divider" part="divider-header"></ctl-divider>
  <div class="body" part="body"><slot></slot></div>
  <ctl-divider class="divider" part="divider-footer"></ctl-divider>
  <footer class="footer" part="footer"><slot name="footer"></slot></footer>
</section>
`;

export class CtlCard extends HTMLElement {
  static get observedAttributes() {
    return ["variant", "padding", "intent", "hoverable", "interactive", "disabled", "dividers", "border", "radius"];
  }

  constructor() {
    super();
    this.attachShadow({ mode: "open" }).appendChild(template.content.cloneNode(true));
    this._card = this.shadowRoot.querySelector(".card");
    this._headerSlot = this.shadowRoot.querySelector('slot[name="header"]');
    this._footerSlot = this.shadowRoot.querySelector('slot[name="footer"]');

    this._onClick = this._onClick.bind(this);
    this._onKeyDown = this._onKeyDown.bind(this);
    this._onHeaderSlotChange = () => this._syncSlots();
    this._onFooterSlotChange = () => this._syncSlots();
  }

  connectedCallback() {
    if (!this.hasAttribute("variant")) this.setAttribute("variant", "outlined");
    if (!this.hasAttribute("padding")) this.setAttribute("padding", "md");
    this._headerSlot.addEventListener("slotchange", this._onHeaderSlotChange);
    this._footerSlot.addEventListener("slotchange", this._onFooterSlotChange);
    this._syncSlots();
    this._sync();
  }

  disconnectedCallback() {
    this._detachInteractive();
    this._headerSlot.removeEventListener("slotchange", this._onHeaderSlotChange);
    this._footerSlot.removeEventListener("slotchange", this._onFooterSlotChange);
  }

  attributeChangedCallback() {
    this._sync();
  }

  // attrs
  get variant() { return this.getAttribute("variant") || "outlined"; }
  set variant(v) {
    const next = ["elevated","filled","ghost","inset","surface"].includes(v) ? v : "outlined";
    this.setAttribute("variant", next);
  }

  get padding() { return this.getAttribute("padding") || "md"; }
  set padding(v) {
    const next = ["none","sm","md","lg"].includes(v) ? v : "md";
    this.setAttribute("padding", next);
  }

  get intent() { return this.getAttribute("intent") || "default"; }
  set intent(v) {
    const next = ["default","primary","secondary","success","warning","danger"].includes(v) ? v : "default";
    this.setAttribute("intent", next);
  }

  get hoverable() { return this.hasAttribute("hoverable"); }
  set hoverable(v) { v ? this.setAttribute("hoverable","") : this.removeAttribute("hoverable"); }

  get interactive() { return this.hasAttribute("interactive"); }
  set interactive(v) { v ? this.setAttribute("interactive","") : this.removeAttribute("interactive"); }

  get disabled() { return this.hasAttribute("disabled"); }
  set disabled(v) { v ? this.setAttribute("disabled","") : this.removeAttribute("disabled"); }

  get dividers() { return this.hasAttribute("dividers"); }
  set dividers(v) { v ? this.setAttribute("dividers","") : this.removeAttribute("dividers"); }

  get border() { return this.getAttribute("border") !== "false"; }
  set border(v) {
    if (v === false) this.setAttribute("border","false");
    else this.removeAttribute("border");
  }

  get radius() { return this.getAttribute("radius") || null; }
  set radius(v) {
    const next = ["sm","md","lg"].includes(v) ? v : null;
    if (next) this.setAttribute("radius", next);
    else this.removeAttribute("radius");
  }

  _syncSlots() {
    const hasHeader = this._headerSlot.assignedNodes({ flatten: true }).length > 0;
    const hasFooter = this._footerSlot.assignedNodes({ flatten: true }).length > 0;
    hasHeader ? this.setAttribute("has-header", "") : this.removeAttribute("has-header");
    hasFooter ? this.setAttribute("has-footer", "") : this.removeAttribute("has-footer");
  }

  _sync() {
    // normalize
    if (!["outlined","elevated","filled","ghost","inset","surface"].includes(this.variant)) this.setAttribute("variant","outlined");
    if (!["none","sm","md","lg"].includes(this.padding)) this.setAttribute("padding","md");
    if (!["default","primary","secondary","success","warning","danger"].includes(this.intent)) this.setAttribute("intent","default");
    const r = this.getAttribute("radius");
    if (r && !["sm","md","lg"].includes(r)) this.removeAttribute("radius");

    if (this.interactive && !this.disabled) {
      this._attachInteractive();
    } else {
      this._detachInteractive();
    }
  }

  _attachInteractive() {
    if (this._interactiveAttached) return;
    this._interactiveAttached = true;

    // focusable target
    this._card.tabIndex = 0;
    this._card.setAttribute("role", "button");
    this._card.setAttribute("aria-disabled", "false");

    this._card.addEventListener("click", this._onClick);
    this._card.addEventListener("keydown", this._onKeyDown);
  }

  _detachInteractive() {
    if (!this._interactiveAttached) return;
    this._interactiveAttached = false;

    this._card.removeEventListener("click", this._onClick);
    this._card.removeEventListener("keydown", this._onKeyDown);

    this._card.removeAttribute("role");
    this._card.removeAttribute("aria-disabled");
    this._card.tabIndex = -1;
  }

  _emitClick(source) {
    this.dispatchEvent(new CustomEvent("ctl-click", {
      bubbles: true,
      composed: true,
      detail: { source }
    }));
  }

  _onClick(e) {
    if (this.disabled) {
      e.preventDefault();
      e.stopPropagation();
      return;
    }
    this._emitClick("pointer");
  }

  _onKeyDown(e) {
    if (this.disabled) return;
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      this._emitClick("keyboard");
    }
  }
}

customElements.define("ctl-card", CtlCard);
